import * as React from "react";
import { Check, ChevronsUpDown, X } from "lucide-react";
import { cn, getInitials } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

export interface MultiSelectOption {
  value: string;
  label: string;
  avatar?: string;
  group?: string;
}

interface MultiSelectProps {
  options: MultiSelectOption[];
  selected: string[];
  onChange: (selected: string[]) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

export function MultiSelect({
  options,
  selected,
  onChange,
  placeholder = "Sélectionner...",
  className,
  disabled = false,
}: MultiSelectProps) {
  const [open, setOpen] = React.useState(false);

  const handleUnselect = (item: string) => {
    onChange(selected.filter((i) => i !== item));
  };

  const handleSelect = (item: string) => {
    if (selected.includes(item)) {
      handleUnselect(item);
    } else {
      onChange([...selected, item]);
    }
  };

  // Group options by group property
  const groupedOptions = React.useMemo(() => {
    const groups: Record<string, MultiSelectOption[]> = {};
    
    options.forEach(option => {
      const groupName = option.group || 'Autres';
      if (!groups[groupName]) {
        groups[groupName] = [];
      }
      groups[groupName].push(option);
    });

    return groups;
  }, [options]);

  const selectedOptions = options.filter(option => selected.includes(option.value));

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn("w-full justify-between min-h-[40px] h-auto focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0", className)}
          disabled={disabled}
        >
          <div className="flex flex-wrap gap-1 max-w-[calc(100%-20px)]">
            {selected.length === 0 ? (
              <span className="text-muted-foreground">{placeholder}</span>
            ) : (
              selectedOptions.map((option) => (
                <div 
                  key={option.value}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded-full bg-background border border-border"
                >
                  <Avatar className="h-5 w-5">
                    <AvatarImage src={option.avatar} alt={option.label} />
                    <AvatarFallback className="text-[10px] bg-muted text-muted-foreground">
                      {getInitials(option.label)}
                    </AvatarFallback>
                  </Avatar>
                  <span className="max-w-[80px] truncate">{option.label}</span>
                  <button
                    type="button"
                    className="ml-1 ring-offset-background rounded-full outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 cursor-pointer hover:bg-muted/50 p-0.5"
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        e.stopPropagation();
                        handleUnselect(option.value);
                      }
                    }}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleUnselect(option.value);
                    }}
                  >
                    <X className="h-3 w-3 text-muted-foreground hover:text-foreground" />
                  </button>
                </div>
              ))
            )}
          </div>
          <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-[var(--radix-popover-trigger-width)] p-0 z-[9999] bg-popover border shadow-lg pointer-events-auto overscroll-contain"
        onWheel={(e) => e.stopPropagation()}
        onTouchStartCapture={(e) => e.stopPropagation()}
        onTouchMoveCapture={(e) => e.stopPropagation()}
        onPointerDownCapture={(e) => e.stopPropagation()}
      >
        <Command className="w-full">
          <CommandInput placeholder="Rechercher..." className="h-9" />
          <CommandList className="max-h-[300px] overflow-y-auto scrollbar-thin scrollbar-thumb-border scrollbar-track-transparent touch-pan-y overscroll-contain">
            <CommandEmpty>Aucun résultat trouvé.</CommandEmpty>
            {Object.entries(groupedOptions).map(([groupName, groupOptions]) => (
              <CommandGroup key={groupName} heading={groupName} className="px-2 py-1.5">
                 {groupOptions.map((option) => {
                   // Create search keywords including all words from the label for better search
                   const searchKeywords = option.label.toLowerCase().split(/\s+/).join(' ');
                   
                   return (
                     <CommandItem
                       key={option.value}
                       value={searchKeywords}
                       onSelect={() => handleSelect(option.value)}
                       className="hover:bg-muted/80 focus:bg-muted/80 cursor-pointer px-2 py-2 rounded-sm"
                     >
                     <div className="flex items-center space-x-2 flex-1">
                       <Avatar className="h-6 w-6">
                         <AvatarImage src={option.avatar} alt={option.label} />
                         <AvatarFallback className="text-xs bg-muted text-muted-foreground">
                           {getInitials(option.label)}
                         </AvatarFallback>
                       </Avatar>
                       <span className="flex-1 text-sm">{option.label}</span>
                       <Check
                         className={cn(
                           "ml-auto h-4 w-4 shrink-0",
                           selected.includes(option.value) ? "opacity-100 text-primary" : "opacity-0"
                         )}
                       />
                      </div>
                   </CommandItem>
                   );
                 })}
              </CommandGroup>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}